import type { Logger } from "@/lib/logger";

/**
 * Per-response telemetry for Medplum HTTP calls.
 *
 * Both fetch wrappers emit through here: `createRetryFetch` (server: actions, API routes,
 * Inngest jobs) and `createMedplumClientSideClient` (provider browsers). One message and one
 * field shape, so a BetterStack metric or alert reads both sides with one query.
 *
 * Medplum's own AuditEvent stream already records every call, but with no HTTP status and with
 * only Medplum's server-side time. This line adds the status and the time the caller waited.
 *
 * Kept free of server-only imports — the browser client bundles it.
 */

export const MEDPLUM_RESPONSE_MESSAGE = "Medplum response";

/** A 2xx at or above this many ms is always logged, so latency alerts see every slow call. */
export const SLOW_MEDPLUM_RESPONSE_MS = 1000;

/**
 * Share of fast 2xx responses that get a line. Medplum serves millions of calls a day across
 * server and browser; logging each one would mostly duplicate the AuditEvent stream.
 * A query estimates the true count as `sum(1 / sampleRate)`.
 */
export const MEDPLUM_SUCCESS_SAMPLE_RATE = 0.02;

/** Low-cardinality description of one request: never the resource id, never the query string. */
export interface MedplumRequestShape {
  /** FHIR interaction (`read`, `search`, `create`, …), an operation (`$graphql`), or a non-FHIR route (`auth/login`). */
  interaction: string;
  /** FHIR resource type, or `null` for system-level and non-FHIR calls. */
  resourceType: string | null;
}

// ─── Request classification ──────────────────────────────────────────────────

const FHIR_RESOURCE_TYPE = /^[A-Z][A-Za-z]*$/;
// route words only - a uuid or numeric id has a digit, so it never matches
const ROUTE_WORD = /^[a-z][a-z-]*$/;

/** Interaction for a call on the type itself: `/fhir/R4/Patient`. */
const TYPE_INTERACTIONS: Record<string, string> = {
  GET: "search",
  POST: "create",
  PUT: "conditional-update",
  PATCH: "conditional-patch",
  DELETE: "conditional-delete",
};

/** Interaction for a call on one instance: `/fhir/R4/Patient/{id}`. */
const INSTANCE_INTERACTIONS: Record<string, string> = {
  GET: "read",
  PUT: "update",
  PATCH: "patch",
  DELETE: "delete",
};

/**
 * Maps a Medplum URL path to a FHIR interaction and resource type.
 * `urlPath` is a pathname (`extractFetchUrlPath`), so it carries no query string.
 */
export function describeMedplumRequest({
  method,
  urlPath,
}: {
  method: string;
  urlPath: string;
}): MedplumRequestShape {
  const segments = urlPath.split("/").filter(Boolean);
  const verb = method.toUpperCase();

  if (segments[0] !== "fhir" || segments[1] !== "R4") {
    return describeNonFhirRequest(segments);
  }

  const [first, second, third] = segments.slice(2);
  const depth = segments.length - 2;

  if (!first) {
    return { interaction: verb === "POST" ? "batch" : "system", resourceType: null };
  }
  if (first.startsWith("$")) {
    return { interaction: first, resourceType: null };
  }
  if (!FHIR_RESOURCE_TYPE.test(first)) {
    return { interaction: "other", resourceType: null };
  }

  const resourceType = first;
  if (!second) {
    return { interaction: TYPE_INTERACTIONS[verb] ?? "other", resourceType };
  }
  if (second === "_search") {
    return { interaction: "search", resourceType };
  }
  if (second === "_history" || second.startsWith("$")) {
    return { interaction: second === "_history" ? "history" : second, resourceType };
  }
  if (!third) {
    return { interaction: INSTANCE_INTERACTIONS[verb] ?? "other", resourceType };
  }
  if (third.startsWith("$")) {
    return { interaction: third, resourceType };
  }
  if (third === "_history") {
    return { interaction: depth === 4 ? "vread" : "history", resourceType };
  }
  // compartment search: Patient/{id}/Observation
  return { interaction: "compartment-search", resourceType };
}

/** `/auth/login` → `auth/login`; an id in the second segment collapses to `other`. */
function describeNonFhirRequest(segments: string[]): MedplumRequestShape {
  const [root = "", route] = segments;
  const routeName = route && ROUTE_WORD.test(route) ? route : "other";
  return { interaction: `${root}/${routeName}`, resourceType: null };
}

// ─── Sampling ────────────────────────────────────────────────────────────────

/**
 * Returns the sample rate a response is logged under, or `undefined` to skip it.
 * Failures and slow successes always log (rate 1); a fast 2xx logs at the success rate.
 */
export function pickMedplumResponseSampleRate({
  status,
  attemptMs,
  random = Math.random,
}: {
  /** `null` when fetch threw before any response arrived. */
  status: number | null;
  attemptMs: number;
  random?: () => number;
}): number | undefined {
  const isSuccess = status !== null && status >= 200 && status < 300;
  if (!isSuccess || attemptMs >= SLOW_MEDPLUM_RESPONSE_MS) {
    return 1;
  }
  return random() < MEDPLUM_SUCCESS_SAMPLE_RATE ? MEDPLUM_SUCCESS_SAMPLE_RATE : undefined;
}

// ─── Emission ────────────────────────────────────────────────────────────────

export interface LogMedplumResponseInput {
  logger: Logger;
  /** Which client made the call (`user`, `background`, `clientside`, …). */
  label: string;
  method: string;
  urlPath: string;
  /** `null` when fetch threw before any response arrived. */
  status: number | null;
  /** Time for this one HTTP attempt. */
  attemptMs: number;
  /** Time since the call started, retries and backoff included. Equals `attemptMs` without retries. */
  elapsedMs: number;
  /** 1-based attempt number within the call. */
  attempt: number;
  /** The client's own `timeoutMs` fired - Medplum was slower than the budget. Server-side only. */
  timedOut?: boolean;
  random?: () => number;
}

/**
 * Emits one `Medplum response` line when the sampling rule keeps the response.
 * 5xx and network failures log at warn; everything else at info.
 */
export function logMedplumResponse({
  logger,
  label,
  method,
  urlPath,
  status,
  attemptMs,
  elapsedMs,
  attempt,
  timedOut = false,
  random,
}: LogMedplumResponseInput): void {
  const sampleRate = pickMedplumResponseSampleRate({ status, attemptMs, random });
  if (sampleRate === undefined) {
    return;
  }

  const meta = {
    label,
    method: method.toUpperCase(),
    ...describeMedplumRequest({ method, urlPath }),
    status,
    attemptMs: Math.round(attemptMs),
    elapsedMs: Math.round(elapsedMs),
    attempt,
    timedOut,
    sampleRate,
  };
  const isServerFault = status === null || status >= 500;

  // Volume signal for BetterStack metrics and alerts. One line is never actionable on its own,
  // so it stays out of Sentry - a retried 503 that then succeeds needs no issue.
  if (isServerFault) {
    logger.warn(MEDPLUM_RESPONSE_MESSAGE, meta, { mirrorToSentry: false });
    return;
  }
  logger.info(MEDPLUM_RESPONSE_MESSAGE, meta, { mirrorToSentry: false });
}
